import React, { Fragment, useMemo, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Grid } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import HiddenContent from 'components/Ui/ScreenReader';
import { Button, Input, Select, Checkbox, Div, Span, P } from 'components/Ui';
import {
  CANADA_REGIONS,
  CREDIT_EXPIRATION_MONTHS,
  CREDIT_EXPIRATION_YEARS,
  US_REGIONS,
} from 'utils/selectOptions';
import { accountIcons, lockIcon } from 'images/index';
import * as actions from 'containers/PrimoProfile/actions';
import {
  selectIsCardSucessful,
  selectIsProcessing,
} from 'containers/PrimoProfile/selectors';
import { creditCard4444, creditCard465 } from 'components/Ui/Input/masks';
import { useExpirationValidation } from 'utils/hooks';

import { selectCountryCode } from 'containers/PrimoAccount/selectors';

import commonStyles from '../../styles.scss';
import styles from '../styles.scss';

const now = moment();
const EditCreditCardForm = ({
  defaultValues = {
    expirationMonth: now.format('MM'),
    expirationYear: now.format('YY'),
    autopay: false,
    saveForFuture: false,
  },
  isNew = false,
  isDefaultPaymentMethod = false,
  isParentSubmit = false,
  onChange,
  clientLocation = 'US',
  isProcessing,
  addCreditCard,
  updateCreditCard,
  isCardSucessful,
  hide: hideDrawer,
}) => {
  const [values, changeValues] = useState(defaultValues);
  const [expirationMonthValue] = useExpirationValidation(values);
  const successRef = useRef(null);

  useEffect(() => {
    if (successRef.current) {
      successRef.current.focus();
    }
  }, [successRef.current]);

  const handleChange = ({ target: { value, name } }) => {
    changeValues({ ...values, [name]: value });
    if (onChange && isParentSubmit) {
      onChange({ name, value });
    }
  };

  const onRadioChange = (value) => {
    changeValues({ ...values, autopay: value });
  };

  const onFutureChange = (value) => {
    changeValues({ ...values, saveForFuture: value });
  };

  const handleSubmit = () => {
    if (isNew) {
      addCreditCard(values);
    } else {
      updateCreditCard(values);
    }
  };

  const FormComponent = isParentSubmit ? Fragment : ValidatorForm;
  const componentProps = isParentSubmit ? {} : { onSubmit: handleSubmit };
  const creditCardMask =
    values.cardNumber && values.cardNumber.startsWith('3')
      ? creditCard465
      : creditCard4444;

  const validationCardProps = useMemo(
    () =>
      isNew
        ? {
            mask: creditCardMask,
            validators: ['required', 'creditCard', 'creditCardType'],
            errorMessages: [
              'Card number is required',
              'Invalid credit card number',
              'Credit card type not accepted',
            ],
          }
        : {},
    [isNew],
  );

  const clientLocationLabel = clientLocation === 'US' ? 'State' : 'Province';
  const stateIsRequiredMessage = `${clientLocationLabel} is required`;
  const headerText = `${isNew ? 'Add new' : 'Edit'} credit/debit card`;

  return (
    <FormComponent {...componentProps}>
      {!isCardSucessful ? (
        <Fragment>
          {!isDefaultPaymentMethod && (
            <Div className={commonStyles.header}>{headerText}</Div>
          )}
          <div className={commonStyles.inputBlock}>
            <Input
              label="Name on card"
              name="cardholderName"
              id="cardholderName"
              ariaLabel="Name on card"
              smallLabel
              value={values.cardholderName}
              onChange={handleChange}
              validators={['required']}
              errorMessages={['Name is required']}
            />
          </div>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <Input
                label="Card number"
                name="cardNumber"
                id="cardNumber"
                ariaLabel="Card number"
                smallLabel
                value={
                  isNew ? values.cardNumber : `************${values.last4}`
                }
                readOnly={!isNew}
                onChange={handleChange}
                {...validationCardProps}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                id="cc-expiration-date-month"
                label="Expiration date"
                value={expirationMonthValue}
                items={CREDIT_EXPIRATION_MONTHS}
                onChange={handleChange}
                name="expirationMonth"
                icon="primaryArrow"
                smallLabel
                validators={['required', 'expMonth']}
                errorMessages={['Date is required', 'Invalid expiration date']}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                id="cc-expiration-date-year"
                value={values.expirationYear}
                items={CREDIT_EXPIRATION_YEARS}
                onChange={handleChange}
                name="expirationYear"
                icon="primaryArrow"
                label="Expiration year"
                smallLabel
                validators={['required']}
                errorMessages={['Year is required']}
              />
            </Grid>
          </Grid>
          <P className={styles.ccFeeDisclaimer}>CC fee/surcharge disclaimer</P>
          <div className={classnames(commonStyles.inputBlock)}>
            <div className={styles.iconNote}>
              <img src={lockIcon} alt="" className={styles.icon} />
              <Span className={styles.note}>
                Your account information is safe and secure.
              </Span>
            </div>
          </div>
          <Div
            className={classnames(commonStyles.header, styles.billingHeader)}
          >
            Billing address
          </Div>
          <div className={commonStyles.inputBlock}>
            <Input
              label="Street address"
              name="address1"
              id="address1"
              ariaLabel="Street address"
              smallLabel
              value={values.address1}
              onChange={handleChange}
              validators={['required']}
              errorMessages={['Street address is required']}
            />
          </div>
          <div className={commonStyles.inputBlock}>
            <Input
              label="Apt/Ste/Other:"
              smallLabel
              name="address2"
              id="address2"
              ariaLabel="Apartment suite other"
              value={values.address2}
              onChange={handleChange}
            />
          </div>
          <div className={commonStyles.inputBlock}>
            <Input
              label="City"
              name="city"
              id="city"
              ariaLabel="City"
              smallLabel
              value={values.city}
              onChange={handleChange}
              validators={['required']}
              errorMessages={['City is required']}
            />
          </div>
          <div className={commonStyles.inputBlock}>
            <Select
              id="cc-state-province"
              label={clientLocationLabel}
              value={values.stateOrProvinceCode}
              items={clientLocation === 'US' ? US_REGIONS : CANADA_REGIONS}
              onChange={handleChange}
              name="stateOrProvinceCode"
              icon="primaryArrow"
              smallLabel
              className={styles.stateSelect}
              validators={['required']}
              errorMessages={[stateIsRequiredMessage]}
            />
            <Input
              label="ZIP code"
              name="postalCode"
              id="postalCode"
              ariaLabel="ZIP code"
              smallLabel
              value={values.postalCode}
              onChange={handleChange}
              validators={['required']}
              errorMessages={['ZIP code is required']}
            />
          </div>

          <div className={styles.checkbox}>
            <Checkbox
              label="Save this card for future payments"
              name="saveForFuture"
              id="saveForFuture"
              isRectangle
              labelClassName={commonStyles.checkbox}
              value={values.saveForFuture}
              onChange={onFutureChange}
            />
          </div>

          <div className={styles.checkbox}>
            <Checkbox
              label="Make this my AutoPay method"
              name="autopay"
              id="autopay"
              isRectangle
              labelClassName={commonStyles.checkbox}
              value={values.autopay}
              onChange={onRadioChange}
            />
          </div>

          {!isParentSubmit && (
            <Fragment>
              <div className={styles.formBottom}>
                <Button
                  className={commonStyles.cancelButton}
                  text="Cancel"
                  onClick={hideDrawer}
                  type="button"
                  disabled={isProcessing}
                />
                <Button
                  className={commonStyles.button}
                  text="Save"
                  data-tracking="Save Credit Card"
                  loading={isProcessing}
                />
              </div>
            </Fragment>
          )}
        </Fragment>
      ) : (
        <div className={styles.submittedDrawer}>
          <div className={styles.submitSuccessContainer}>
            <div className={styles.submitSuccess}>
              <img src={accountIcons.Finger} alt="finger" />
            </div>
            <Div
              className={styles.submitSuccess}
              ref={successRef}
              tabIndex="-1"
            >
              Your payment method has been saved.
            </Div>
          </div>
        </div>
      )}
      <HiddenContent>
        <Input
          label="State"
          name="stateOrProvinceCode"
          ariaLabel="State"
          value={values.stateOrProvinceCode}
          onChange={handleChange}
        />
      </HiddenContent>
    </FormComponent>
  );
};

const mapStateToProps = createStructuredSelector({
  isProcessing: selectIsProcessing(),
  isCardSucessful: selectIsCardSucessful(),
  clientLocation: selectCountryCode(),
});

const mapDispatchToProps = {
  addCreditCard: actions.addCreditCard,
  updateCreditCard: actions.updateCreditCard,
  resetPayMyBillError: actions.resetPayMyBillError,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditCreditCardForm);
